import {
  type ProductListQuery,
  productListQuerySchema,
  productStockFilterSchema,
  productTypeSchema,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

export const PRODUCT_PAGE_SIZES = [25, 50, 100] as const

/** The sorts the API allows on the product list; `onHand` needs inventory.read. */
export const PRODUCT_SORT_FIELDS = [
  'name',
  'createdAt',
  'price',
  'onHand',
  'sku',
  'type',
  'category',
] as const
type ProductSortField = (typeof PRODUCT_SORT_FIELDS)[number]
export type ProductSort = ProductSortField | `-${ProductSortField}`

export function isProductSort(value: string): value is ProductSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PRODUCT_SORT_FIELDS as readonly string[]).includes(field)
}

/** Sorting by on hand needs inventory.read (the API answers 403 otherwise). */
export function isStockSort(sort: string | undefined): boolean {
  return sort === 'onHand' || sort === '-onHand'
}

export const productViewSchema = productListQuerySchema.shape.view.unwrap()
export type ProductView = z.infer<typeof productViewSchema>
export type ProductStockView = z.infer<typeof productStockFilterSchema>

/**
 * The products list lives in the URL (shareable, back-button safe). Every key is optional and an
 * invalid value silently falls back to the default; defaults are applied by `toProductListQuery`.
 */
export const productSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof PRODUCT_PAGE_SIZES)[number] =>
      (PRODUCT_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  sort: z.string().refine(isProductSort).optional().catch(undefined),
  // A scanned or hand-written barcode (`?q=6291100100017`) arrives as a number: keep it as text.
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  view: productViewSchema.optional().catch(undefined),
  /** Low stock / Out of stock (need inventory.read): the current products narrowed by stock. */
  stock: productStockFilterSchema.optional().catch(undefined),
  type: productTypeSchema.optional().catch(undefined),
  categoryId: z.uuid().optional().catch(undefined),
  /** On hand and the stock views read this warehouse (all warehouses when absent). */
  warehouseId: z.uuid().optional().catch(undefined),
})
export type ProductSearch = z.infer<typeof productSearchSchema>

export type ProductListParams = Omit<ProductListQuery, 'page' | 'pageSize' | 'view'> & {
  page: number
  pageSize: number
  view: ProductView
}

export function toProductListQuery(search: ProductSearch): ProductListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PRODUCT_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    // A stock view is the current products narrowed by stock: it never pairs with another view.
    view: search.stock ? 'current' : (search.view ?? 'current'),
    stock: search.stock,
    type: search.type,
    categoryId: search.categoryId,
    warehouseId: search.warehouseId,
  }
}

// ── the product 360 ──────────────────────────────────────────────────────────

export const PRODUCT_TABS = ['overview', 'stock', 'serials', 'sales', 'purchases', 'attachments'] as const
export type ProductTabId = (typeof PRODUCT_TABS)[number]

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/**
 * The product 360 keeps its state in the URL: the open tab; on the Stock tab the warehouse and
 * variant the movements are narrowed to; on the history tabs the variant, the period and the page.
 */
export const productProfileSearchSchema = z.object({
  tab: z.enum(PRODUCT_TABS).optional().catch(undefined),
  warehouseId: z.uuid().optional().catch(undefined),
  variantId: z.uuid().optional().catch(undefined),
  from: isoDate,
  to: isoDate,
  page: z.number().int().min(1).optional().catch(undefined),
})
export type ProductProfileSearch = z.infer<typeof productProfileSearchSchema>

/** Label sheet options (`/products/:id/labels`): which variant, and whether to print at once. */
export const productLabelsSearchSchema = z.object({
  variantId: z.uuid().optional().catch(undefined),
  print: z.coerce.boolean().optional().catch(undefined),
})
